import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, TrendingUp, Calendar, BarChart3 } from "lucide-react";

const MONTH_NAMES_EL = [
  "Ιαν", "Φεβ", "Μάρ", "Απρ", "Μάι", "Ιούν",
  "Ιούλ", "Αύγ", "Σεπ", "Οκτ", "Νοέ", "Δεκ",
];
const DAY_NAMES_EL = ["Κυρ", "Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ"];

const formatMonth = (key) => {
  const [y, m] = key.split("-").map((x) => parseInt(x, 10));
  return `${MONTH_NAMES_EL[m - 1]} ${y}`;
};
const formatShortDate = (key) => {
  const d = new Date(key + "T00:00:00Z");
  return `${DAY_NAMES_EL[d.getUTCDay()]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-[#EBE5DF] p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#736B66] mb-3">
      <Icon size={14} className="text-[#D4AF37]" />
      {label}
    </div>
    <div className="text-3xl font-serif text-[#2C1E16]">{value}</div>
    {sub && <div className="text-xs text-[#736B66] mt-1">{sub}</div>}
  </div>
);

const Bar = ({ value, max, label, sub }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={`bar-${label}`}>
      <div className="w-20 text-xs text-[#736B66] shrink-0">{label}</div>
      <div className="flex-1 bg-[#F5F2ED] h-7 relative overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B8941F] transition-all"
          style={{ width: `${pct}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-semibold">
          <span className="text-[#2C1E16]">{value}</span>
          <span className="text-[#736B66]">{sub}</span>
        </div>
      </div>
    </div>
  );
};

const AdminVisitorStats = () => {
  const [stats, setStats] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [pageWindow, setPageWindow] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/visits/stats"),
      api.get(`/admin/visits/top-pages?days=${pageWindow}&limit=20`),
    ])
      .then(([s, p]) => {
        setStats(s.data);
        setTopPages(p.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageWindow]);

  if (loading) return <div className="text-[#736B66]">Loading...</div>;
  if (!stats) return <div className="text-[#736B66]">No data available</div>;

  const dailyMax = Math.max(...stats.this_week.daily.map((d) => d.total), 1);
  const monthlyMax = Math.max(...stats.last_6_months.map((m) => m.total), 1);

  return (
    <div className="space-y-8 max-w-5xl" data-testid="visitor-stats">
      <h3 className="font-serif text-2xl">Επισκεψιμότητα</h3>

      {/* Top cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat
          icon={Calendar}
          label="Σήμερα"
          value={stats.today.total}
          sub={`${stats.today.unique} μοναδικοί επισκέπτες`}
        />
        <Stat
          icon={TrendingUp}
          label="Αυτή την εβδομάδα"
          value={stats.this_week.total}
          sub={`${stats.this_week.unique} μοναδικοί επισκέπτες`}
        />
        <Stat
          icon={Users}
          label="Τελευταίοι 6 μήνες"
          value={stats.last_6_months.reduce((acc, m) => acc + m.total, 0)}
          sub="συνολικά views"
        />
      </div>

      {/* Weekly breakdown */}
      <div className="bg-white border border-[#EBE5DF] p-6" data-testid="weekly-breakdown">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#736B66] mb-5">
          <BarChart3 size={14} className="text-[#D4AF37]" />
          Εβδομαδιαία ανάλυση (τελευταίες 7 ημέρες)
        </div>
        <div className="space-y-2">
          {stats.this_week.daily.map((d) => (
            <Bar
              key={d.date}
              label={formatShortDate(d.date)}
              value={d.total}
              max={dailyMax}
              sub={`${d.unique} unique`}
            />
          ))}
        </div>
      </div>

      {/* Monthly breakdown */}
      <div className="bg-white border border-[#EBE5DF] p-6" data-testid="monthly-breakdown">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#736B66] mb-5">
          <BarChart3 size={14} className="text-[#D4AF37]" />
          Μηνιαία σύγκριση (τελευταίοι 6 μήνες)
        </div>
        <div className="space-y-2">
          {stats.last_6_months.map((m) => (
            <Bar
              key={m.month}
              label={formatMonth(m.month)}
              value={m.total}
              max={monthlyMax}
              sub={`${m.unique} unique`}
            />
          ))}
        </div>
      </div>

      <p className="text-xs text-[#736B66]">
        Σημείωση: Μοναδικοί επισκέπτες προσδιορίζονται από hashed IP. Κάθε browser
        session καταγράφεται μία φορά την ημέρα.
      </p>

      {/* Top pages */}
      <div className="bg-white border border-[#EBE5DF] p-6" data-testid="top-pages">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#736B66]">
            <BarChart3 size={14} className="text-[#D4AF37]" />
            Δημοφιλέστερες σελίδες
          </div>
          <select
            value={pageWindow}
            onChange={(e) => setPageWindow(parseInt(e.target.value, 10))}
            className="bg-transparent text-xs tracking-widest border border-[#EBE5DF] px-3 py-1 cursor-pointer"
            data-testid="top-pages-window"
          >
            <option value={1}>Τελευταία 24h</option>
            <option value={7}>Τελευταίες 7 ημέρες</option>
            <option value={30}>Τελευταίες 30 ημέρες</option>
            <option value={90}>Τελευταίοι 3 μήνες</option>
          </select>
        </div>
        {topPages && topPages.pages && topPages.pages.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#EBE5DF] text-xs uppercase tracking-widest text-[#736B66]">
                <th className="text-left py-2">Σελίδα</th>
                <th className="text-left py-2">Τύπος</th>
                <th className="text-right py-2">Views</th>
                <th className="text-right py-2">Unique</th>
              </tr>
            </thead>
            <tbody>
              {topPages.pages.map((p) => (
                <tr key={p.path} className="border-b border-[#EBE5DF]/50" data-testid={`top-page-row`}>
                  <td className="py-2 font-mono text-xs truncate max-w-[300px]">{p.path}</td>
                  <td className="py-2">
                    {p.page_type ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#D4AF37]/10 text-[#7A6017] rounded">
                        {p.page_type}
                      </span>
                    ) : (
                      <span className="text-[#736B66]">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right font-semibold">{p.total}</td>
                  <td className="py-2 text-right text-[#736B66]">{p.unique}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-[#736B66] text-center py-4">
            Δεν υπάρχουν ακόμα δεδομένα. Οι σελίδες θα εμφανιστούν εδώ μόλις δεχτούν επισκέψεις.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminVisitorStats;
